import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Loader2, Target, UserRound, Briefcase, Clock, Banknote, MapPin } from "lucide-react";
import QualificationInput from "../components/QualificationInput";
import { toast } from "sonner";

const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Locum"];
const SHIFTS = ["Day", "Night", "Rotating", "Flexible"];

const emptyPref = {
  desired_role: "",
  employment_types: [],
  shift_preference: "",
  preferred_locations: "",
  preferred_qualifications: "",
  expected_salary: "",
  start_availability: "",
  open_to_relocation: false,
  notes: "",
};

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors ${
      active
        ? "border-[#16A34A] bg-[#16A34A]/10 text-[#16A34A]"
        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
    }`}
  >
    {children}
  </button>
);

const SectionTitle = ({ icon: Icon, title, desc, tint }) => (
  <div className="mb-5 flex items-start gap-3">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tint}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <h2 className="font-heading text-xl font-bold text-[#0B2545]">{title}</h2>
      <p className="text-sm text-slate-500">{desc}</p>
    </div>
  </div>
);

const ProfilePreferences = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", qualification: "", location: "", experience_years: 0,
    skills: "", bio: "", availability: "Open to opportunities",
  });
  const [pref, setPref] = useState(emptyPref);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user?.role === "professional") {
      api.get("/professionals/me").then((r) => {
        if (r.data) {
          setForm({
            title: r.data.title || "",
            qualification: r.data.qualification || "",
            location: r.data.location || "",
            experience_years: r.data.experience_years || 0,
            skills: (r.data.skills || []).join(", "),
            bio: r.data.bio || "",
            availability: r.data.availability || "Open to opportunities",
          });
          const p = r.data.preferences || {};
          setPref({
            desired_role: p.desired_role || "",
            employment_types: p.employment_types || [],
            shift_preference: p.shift_preference || "",
            preferred_locations: (p.preferred_locations || []).join(", "),
            preferred_qualifications: (p.preferred_qualifications || []).join(", "),
            expected_salary: p.expected_salary || "",
            start_availability: p.start_availability || "",
            open_to_relocation: !!p.open_to_relocation,
            notes: p.notes || "",
          });
        }
      }).finally(() => setLoaded(true));
    } else {
      setLoaded(true);
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setP = (k) => (e) => setPref({ ...pref, [k]: e.target.value });

  const toggleType = (t) =>
    setPref((p) => ({
      ...p,
      employment_types: p.employment_types.includes(t)
        ? p.employment_types.filter((x) => x !== t)
        : [...p.employment_types, t],
    }));

  const setShift = (s) =>
    setPref((p) => ({ ...p, shift_preference: p.shift_preference === s ? "" : s }));

  const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast("Please register as a professional to save", { description: "It only takes a moment." });
      navigate("/register");
      return;
    }
    if (user.role !== "professional") {
      toast.error("Only professionals can save a profile");
      return;
    }
    setSaving(true);
    try {
      await api.post("/professionals/profile", {
        title: form.title,
        qualification: form.qualification,
        location: form.location,
        experience_years: parseInt(form.experience_years) || 0,
        skills: toArr(form.skills),
        bio: form.bio,
        availability: form.availability,
        preferences: {
          desired_role: pref.desired_role,
          employment_types: pref.employment_types,
          shift_preference: pref.shift_preference,
          preferred_locations: toArr(pref.preferred_locations),
          preferred_qualifications: toArr(pref.preferred_qualifications),
          expected_salary: pref.expected_salary,
          start_availability: pref.start_availability,
          open_to_relocation: pref.open_to_relocation,
          notes: pref.notes,
        },
      });
      toast.success("Saved!", { description: "Your profile and preferences are live in the directory." });
      navigate("/professionals");
    } catch (err) {
      // Handle FastAPI 422 validation errors (detail is an array)
      const detail = err?.response?.data?.detail;
      let errorMsg = "Could not save";
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(", ");
      } else if (typeof detail === "string") {
        errorMsg = detail;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !loaded)
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#16A34A]" /></div></PageShell>;

  return (
    <PageShell>
      <div className="mx-auto max-w-[820px] px-6 py-10 lg:px-8">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A]/10 px-3 py-1 text-xs font-semibold text-[#16A34A]">
          <Target className="h-3.5 w-3.5" /> For professionals
        </span>
        <h1 className="mt-3 font-heading text-[34px] font-extrabold text-[#0B2545] sm:text-[40px]">Tell Us What You're Looking For</h1>
        <p className="mt-2 text-[16px] text-slate-600">Share your details and preferences so the right organisations can find you.</p>

        {(!user || user.role !== "professional") && (
          <div className="mt-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-amber-800">
              {!user
                ? "You're previewing this page. Register as a professional to save your preferences."
                : "You're signed in as an organisation — this page is for professionals to fill in."}
            </p>
            {!user && (
              <Button type="button" onClick={() => navigate("/register")} className="h-9 shrink-0 rounded-lg bg-[#16A34A] px-4 text-sm font-semibold text-white hover:bg-[#138a3f]">
                Register as professional
              </Button>
            )}
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-6">
          {/* ---- About you ---- */}
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7">
            <SectionTitle icon={UserRound} title="About You" desc="Your professional profile" tint="bg-[#1E4FA3]/10 text-[#1E4FA3]" />
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Profession / title *</Label>
                  <Input id="title" required value={form.title} onChange={set("title")} className="mt-1.5 h-11" placeholder="e.g. Registered Nurse" />
                </div>
                <div>
                  <Label htmlFor="qualification">Qualification *</Label>
                  <QualificationInput id="qualification" required value={form.qualification} onChange={set("qualification")} className="mt-1.5 h-11" placeholder="e.g. BSc Nursing, MBBS, MD Cardiology" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="location">Current location *</Label>
                  <Input id="location" required value={form.location} onChange={set("location")} className="mt-1.5 h-11" placeholder="e.g. Mumbai, India" />
                </div>
                <div>
                  <Label htmlFor="years">Years of experience</Label>
                  <Input id="years" type="number" min={0} value={form.experience_years} onChange={set("experience_years")} className="mt-1.5 h-11" />
                </div>
              </div>
              <div>
                <Label htmlFor="skills">Skills (comma separated)</Label>
                <Input id="skills" value={form.skills} onChange={set("skills")} className="mt-1.5 h-11" placeholder="e.g. ICU, Emergency Care, IV Therapy" />
              </div>
              <div>
                <Label htmlFor="bio">About you</Label>
                <Textarea id="bio" value={form.bio} onChange={set("bio")} rows={3} className="mt-1.5" placeholder="A short summary of your experience..." />
              </div>
            </div>
          </div>

          {/* ---- What you're looking for ---- */}
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-7">
            <SectionTitle icon={Target} title="What You're Looking For" desc="Your ideal next role" tint="bg-[#16A34A]/10 text-[#16A34A]" />
            <div className="space-y-5">
              <div>
                <Label htmlFor="desired_role">Desired role</Label>
                <Input id="desired_role" value={pref.desired_role} onChange={setP("desired_role")} className="mt-1.5 h-11" placeholder="e.g. Senior ICU Nurse" />
              </div>

              <div>
                <Label className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-slate-400" /> Employment type <span className="font-normal text-slate-400">(select all that apply)</span></Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {EMP_TYPES.map((t) => (
                    <Chip key={t} active={pref.employment_types.includes(t)} onClick={() => toggleType(t)}>{t}</Chip>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> Preferred shift</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SHIFTS.map((s) => (
                    <Chip key={s} active={pref.shift_preference === s} onClick={() => setShift(s)}>{s}</Chip>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="pref_loc" className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> Preferred locations</Label>
                  <Input id="pref_loc" value={pref.preferred_locations} onChange={setP("preferred_locations")} className="mt-1.5 h-11" placeholder="e.g. Mumbai, Delhi" />
                </div>
                <div>
                  <Label htmlFor="pref_spec">Preferred qualifications</Label>
                  <QualificationInput id="pref_spec" listId="pref-qualification-options" value={pref.preferred_qualifications} onChange={setP("preferred_qualifications")} className="mt-1.5 h-11" placeholder="e.g. BSc Nursing, MD" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="salary" className="flex items-center gap-1.5"><Banknote className="h-4 w-4 text-slate-400" /> Expected salary</Label>
                  <Input id="salary" value={pref.expected_salary} onChange={setP("expected_salary")} className="mt-1.5 h-11" placeholder="e.g. ₹8,00,000 / year" />
                </div>
                <div>
                  <Label htmlFor="start">Earliest start</Label>
                  <Input id="start" value={pref.start_availability} onChange={setP("start_availability")} className="mt-1.5 h-11" placeholder="e.g. Immediately / 1 month" />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-[#0B2545]">Open to relocation</p>
                  <p className="text-xs text-slate-500">Happy to consider roles in other cities</p>
                </div>
                <Switch
                  checked={pref.open_to_relocation}
                  onCheckedChange={(v) => setPref({ ...pref, open_to_relocation: v })}
                  className="data-[state=checked]:bg-[#16A34A]"
                />
              </div>

              <div>
                <Label htmlFor="notes">Anything else? (what matters most to you)</Label>
                <Textarea id="notes" value={pref.notes} onChange={setP("notes")} rows={3} className="mt-1.5" placeholder="e.g. Looking for career growth, good work-life balance, mentorship..." />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="h-12 w-full rounded-xl bg-[#16A34A] text-[15px] font-semibold text-white hover:bg-[#138a3f]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save & Publish
          </Button>
        </form>
      </div>
    </PageShell>
  );
};

export default ProfilePreferences;
